import React, { useState } from 'react';
import {
    Paper,
    Typography,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    CircularProgress
} from '@mui/material';
import BarcodeScanner from './BarcodeScanner';

/**
 * Return Entry Form component
 * 
 * @param {Object} props - Component props
 * @param {function} props.onSubmit - Function to call when form is submitted
 * @param {boolean} props.loading - Whether the form is in a loading state
 * @param {Array} props.marketplaceOptions - Array of marketplace options
 * @param {Array} props.companyOptions - Array of company options
 * @returns {JSX.Element} - The return entry form
 */
const ReturnEntryForm = ({
    onSubmit,
    loading = false,
    marketplaceOptions = [],
    companyOptions = []
}) => {
    const [formData, setFormData] = useState({
        marketplace: '',
        company: '', 
        AWB: ''
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);

        // Only reset the AWB field after submission, keeping marketplace and company selections
        setFormData({
            marketplace: formData.marketplace,
            company: formData.company,
            AWB: ''
        });
    };

    const handleScan = (scannedValue) => {
        setFormData({
            ...formData,
            AWB: scannedValue
        });
    };

    return (
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" component="h2" gutterBottom>
                Add Return Entry
            </Typography>

            <form onSubmit={handleSubmit}>
                <Grid container spacing={3} alignItems="center">
                    {/* Marketplace */}
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth required size="medium" sx={{ minWidth: '120px' }}>
                            <InputLabel id="marketplace-select-label">Marketplace</InputLabel>
                            <Select
                                labelId="marketplace-select-label"
                                name="marketplace"
                                value={formData.marketplace}
                                label="Marketplace"
                                onChange={handleInputChange}
                                sx={{ height: '56px' }}
                                displayEmpty
                            >
                                {marketplaceOptions.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Company */}
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth required size="medium" sx={{ minWidth: '120px' }}>
                            <InputLabel id="company-select-label">Company</InputLabel>
                            <Select
                                labelId="company-select-label"
                                name="company"
                                value={formData.company}
                                label="Company"
                                sx={{ height: '56px' }}
                                displayEmpty
                                onChange={handleInputChange}
                            >
                                {companyOptions.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Barcode Scanner */}
                    <Grid item xs={12} sm={12} md={4}>
                        <BarcodeScanner
                            fullWidth
                            required
                            label="AWB / Tracking Number"
                            name="AWB"
                            value={formData.AWB}
                            onChange={handleInputChange}
                            onScan={handleScan}
                            disabled={loading}
                        />
                    </Grid>

                    {/* Submit Button */}
                    <Grid item xs={12} sm={12} md={2}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={
                                loading ||
                                !formData.marketplace ||
                                !formData.company || // fixed name
                                !formData.AWB
                            }
                            sx={{ height: '56px' }} // match text field height
                        >
                            {loading ? <CircularProgress size={24} /> : 'Submit'}
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Paper>
    );
};

export default ReturnEntryForm;
